import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { Search, ChevronDown, Loader2, Trash2 } from "lucide-react";
import Select from "react-select";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../../store/slices/leadSlice";
import { fetchRawMaterialsThunk } from "../../store/slices/warehouseSlice";
import { getUnitsByType } from "../../utils/warehouse.util";
import { UNITS } from "../../models/warehouseRawMaterial.model";

interface Option {
  value: string;
  label: string;
  unit?: string;
}

interface ItemRowData {
  itemId: string;
  itemType: "material" | "product";
  quantity: number;
  unit: string;
}

interface ProductMaterialSelectProps {
  item: ItemRowData;
  index: number;
  handleTypeChange: (index: number, newType: "material" | "product") => void;
  handleSelectItem: (
    index: number,
    selected: { value: string; label: string; unit?: string },
  ) => void;
  handleItemChange: (
    index: number,
    field: keyof ItemRowData,
    value: any,
  ) => void;
  handleRemoveItem: (index: number) => void;
  canRemove: boolean;
  placeholder?: string;
}

export function ProductMaterialSelect({
  item,
  index,
  handleTypeChange,
  handleSelectItem,
  handleItemChange,
  handleRemoveItem,
  canRemove,
  placeholder = "Select Item...",
}: ProductMaterialSelectProps) {
  const dispatch: RootDispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Floating portal menu coordinates
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Redux Products State
  const reduxProducts = useSelector(
    (state: RootState) => state.product.products,
  );
  const rawMaterials = useSelector(
    (state: RootState) => state.warehouse.rawMaterials,
  );

  // Fetch products on load if product is selected
  useEffect(() => {
    if (item.itemType === "product" && reduxProducts.length === 0) {
      dispatch(fetchProducts());
    }
  }, [item.itemType, dispatch, reduxProducts.length]);

  // Fetch materials with pagination & search
  const loadMaterials = useCallback(
    async (pageNum: number, searchQuery: string, isNewSearch: boolean) => {
      setLoading(true);
      try {
        const result = await dispatch(
          fetchRawMaterialsThunk({
            page: pageNum,
            limit: 20,
            search: searchQuery,
            bom: false,
            sortBy: "name",
            sortOrder: "asc",
            from: "factory",
          }),
        ).unwrap();

        const formatted = (result.data || []).map((p: any) => ({
          value: p._id,
          label: p.name,
          unit: p.unit,
        }));

        setOptions((prev) =>
          isNewSearch ? formatted : [...prev, ...formatted],
        );
        setHasMore(result.data.length === 20);
      } catch (err) {
        if (isNewSearch) setOptions([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  // Synchronize options when itemType or search query changes
  useEffect(() => {
    setPage(1);
    if (item.itemType === "product") {
      const filtered = reduxProducts
        .filter((p: any) =>
          p.name?.toLowerCase().includes(search.toLowerCase()),
        )
        .map((p: any) => ({
          value: p._id,
          label: p.name,
          unit: p.unit || "pcs",
        }));
      setOptions(filtered);
      setHasMore(false);
    } else if (item.itemType === "material") {
      loadMaterials(1, search, true);
    }
  }, [item.itemType, search, reduxProducts, loadMaterials]);

  // Handle position tracking for portal menu
  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Lazy loading scroll handler
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 20 &&
      hasMore &&
      !loading &&
      item.itemType === "material"
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadMaterials(nextPage, search, false);
    }
  };

  // Close floating dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === item.itemId);

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      padding: "0.1rem",
      fontSize: "0.75rem",
      borderRadius: "0.5rem",
      borderColor: "#E2E8F0",
      minHeight: "2.25rem",
      boxShadow: "none",
      "&:hover": { borderColor: "#CBD5E1" },
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999, // Ensure it sits above modal overlays
    }),
    menu: (provided: any) => ({
      ...provided,
      fontSize: "0.75rem",
    }),
  };

  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 rounded-xl border border-slate-200 p-3 bg-slate-50/50 w-full">
      {/* Item Type Selector */}
      <select
        value={item.itemType}
        onChange={(e) =>
          handleTypeChange(index, e.target.value as "material" | "product")
        }
        className="w-32 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none focus:border-indigo-500 font-medium"
      >
        <option value="material">Material</option>
        <option value="product">Product</option>
      </select>

      {/* Searchable Dropdown Trigger Control */}
      <div className="flex-1 min-w-[200px] relative">
        <div
          ref={triggerRef}
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs cursor-pointer focus:border-indigo-500 shadow-xs"
        >
          <span
            className={
              selectedOption ? "text-slate-800 font-medium" : "text-slate-400"
            }
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown size={14} className="text-slate-400" />
        </div>
      </div>

      {/* Quantity Input */}
      <input
        type="number"
        min="1"
        placeholder="Qty"
        value={item.quantity}
        onChange={(e) =>
          handleItemChange(index, "quantity", Number(e.target.value))
        }
        className="w-24 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs outline-none focus:border-indigo-500"
        required
      />

      {/* Unit Selector with Portal Integration */}
      <div className="w-32">
        <Select
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
          menuPosition="fixed"
          options={
            item.itemId
              ? getUnitsByType(
                  rawMaterials.find((r) => r._id === item.itemId)?.unitType ||
                    "",
                )
              : UNITS
          }
          value={
            item.unit ? UNITS.find((u) => u.value === item.unit) || null : null
          }
          onChange={(opt) => handleItemChange(index, "unit", opt?.value || "")}
          placeholder="Unit"
          styles={selectStyles}
        />
      </div>

      {/* Remove Button */}
      {canRemove && (
        <button
          type="button"
          onClick={() => handleRemoveItem(index)}
          className="text-slate-400 hover:text-rose-500 p-1"
        >
          <Trash2 size={16} />
        </button>
      )}

      {/* Item Options Overlay rendered in document.body via Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "absolute",
              top: `${coords.top + 4}px`,
              left: `${coords.left}px`,
              width: `${coords.width}px`,
            }}
            className="z-[9999] rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-100"
          >
            {/* Search Box */}
            <div className="p-2 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
              <Search size={14} className="text-slate-400" />
              <input
                type="text"
                className="w-full bg-transparent text-xs outline-none text-slate-700"
                placeholder={`Search ${item.itemType}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            {/* List Container with Scroll Listener */}
            <div
              onScroll={handleScroll}
              className="max-h-52 overflow-y-auto divide-y divide-slate-50"
            >
              {options.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => {
                    handleSelectItem(index, opt);
                    setIsOpen(false);
                  }}
                  className={`px-3 py-2 text-xs cursor-pointer hover:bg-indigo-50 hover:text-indigo-600 transition ${
                    opt.value === item.itemId
                      ? "bg-indigo-50 font-semibold text-indigo-600"
                      : "text-slate-700"
                  }`}
                >
                  {opt.label}
                </div>
              ))}

              {loading && (
                <div className="p-2 flex justify-center text-indigo-600">
                  <Loader2 size={16} className="animate-spin" />
                </div>
              )}

              {!loading && options.length === 0 && (
                <div className="p-3 text-center text-xs text-slate-400">
                  No items found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
